import React from 'react';
import { Form, Modal, message } from 'antd';
import { RichTextFormField } from '../../components/RichTextEditor';
import {
	useCreateProgramFeedbackMutation,
	useUpdateProgramFeedbackMutation,
} from '../../redux/api/manufacturing';
import { useProgramImages } from './useProgramImages';
import type { ProgramFeedback } from '../../interfaces/CamProgramming.interface';

interface FeedbackFormValues {
	body: string;
}

interface FeedbackFormModalProps {
	programId: string;
	/** The entry being edited, or null to log new feedback. */
	entry: ProgramFeedback | null;
	open: boolean;
	onClose: () => void;
}

const FeedbackFormModal: React.FC<FeedbackFormModalProps> = ({
	programId,
	entry,
	open,
	onClose,
}) => {
	const [form] = Form.useForm<FeedbackFormValues>();
	const [createFeedback, { isLoading: isCreating }] =
		useCreateProgramFeedbackMutation();
	const [updateFeedback, { isLoading: isUpdating }] =
		useUpdateProgramFeedbackMutation();
	const images = useProgramImages(programId);

	const handleSubmit = async (values: FeedbackFormValues) => {
		try {
			if (entry) {
				await updateFeedback({
					id: entry.id,
					programId,
					values,
				}).unwrap();
				message.success('Feedback updated');
			} else {
				await createFeedback({
					program: programId,
					...values,
				}).unwrap();
				message.success('Feedback logged');
			}
			onClose();
		} catch {
			message.error('Failed to save feedback');
		}
	};

	return (
		<Modal
			className="cam-modal"
			title={entry ? 'Edit Feedback' : 'Log Feedback'}
			open={open}
			centered
			onCancel={onClose}
			onOk={() => form.submit()}
			okText={entry ? 'Save' : 'Log'}
			confirmLoading={isCreating || isUpdating}
			width={780}
			destroyOnClose
		>
			<Form
				form={form}
				layout="vertical"
				onFinish={handleSubmit}
				initialValues={{
					body: entry?.body || '',
				}}
			>
				<Form.Item
					name="body"
					label="Feedback"
					rules={[
						{
							validator: (_, value: string) =>
								value &&
								(value.replace(/<[^>]*>/g, '').trim() || value.includes('<img'))
									? Promise.resolve()
									: Promise.reject(new Error('Write some feedback')),
						},
					]}
				>
					<RichTextFormField
						{...images}
						placeholder="Notes, observations, tips... Paste or drop images directly."
						minHeight={200}
					/>
				</Form.Item>
			</Form>
		</Modal>
	);
};

export default FeedbackFormModal;
